import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;

function ContactSupport({ onBack, initialType }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    support_type: initialType || 'general'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  // Update form data when initialType changes
  React.useEffect(() => {
    if (initialType) {
      setFormData(prev => ({
        ...prev,
        support_type: initialType
      }));
    }
  }, [initialType]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/support/submit`, formData);
      
      if (response.data.status === 'success') {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Support submission error:', error);
      setError('Failed to submit support request. Please try again or email support@actuvo.ai directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gray-800 rounded-lg shadow-xl p-8">
            {/* Back Button */}
            <div className="mb-6">
              <button
                onClick={handleBack}
                className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
              >
                <span className="mr-2">←</span>
                Back
              </button>
            </div>

            <div className="text-center">
              <div className="text-6xl mb-6">✅</div>
              <h1 className="text-3xl font-bold text-green-400 mb-4">
                Support Request Submitted!
              </h1>
              <p className="text-gray-300 text-lg mb-6">
                Thank you for contacting actuvo.ai support. We've received your request and will respond within 24-48 hours.
              </p>
              <div className="bg-blue-900 border border-blue-700 p-4 rounded-lg mb-6">
                <p className="text-blue-100 text-sm">
                  📧 You'll receive a confirmation email shortly with your request details and expected response time.
                </p>
              </div>
              <button
                onClick={handleBack}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Back Button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span className="mr-2">←</span>
              Back
            </button>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-4">
              Contact Support
            </h1>
            <p className="text-gray-300">
              Need help with actuvo.ai? Send us a message and we'll get back to you quickly.
            </p>
          </div>

          {error && (
            <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="support_type" className="block text-sm font-medium text-gray-300 mb-2">
                Support Type
              </label>
              <select
                id="support_type"
                name="support_type"
                value={formData.support_type}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
              >
                <option value="general">General Support</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Question</option>
                <option value="legal">Legal Inquiry</option>
                <option value="privacy">Privacy Question</option>
              </select>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                required
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Brief description of your issue"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows="6"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Please provide as much detail as possible about your issue or question..."
              />
            </div>

            <div className="bg-yellow-900 border border-yellow-700 p-4 rounded-lg">
              <p className="text-yellow-100 text-sm">
                <strong>💡 Before submitting:</strong> Check your Hyperliquid account for trade history, 
                review your API credential settings, and ensure your subscription is active.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Support Request'
                )}
              </button>

              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Other Ways to Contact Us</h3>
            <div className="space-y-2 text-sm text-gray-400">
              <p><strong>General Support:</strong> support@actuvo.ai</p>
              <p><strong>Legal Questions:</strong> legal@actuvo.ai</p>
              <p><strong>Privacy Inquiries:</strong> privacy@actuvo.ai</p>
              <p className="mt-4 text-xs">
                💡 For fastest response, please use the form above. Email responses typically take 24-48 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactSupport;